import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { AddressFields } from '@/components/common/AddressFields';
import { ImageUploadField } from '@/components/common/ImageUploadField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TenantSelect, OrganizationSelect } from '../_shared/EntitySelect';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { platformWarehousesService } from '@/services/platform/warehouses';
import { platformUsersService } from '@/services/platform/users';
import type {
  PlatformWarehouse,
  PlatformWarehouseCreatePayload,
  PlatformWarehouseType,
} from '@/types/platform';

const WAREHOUSE_TYPES: { value: PlatformWarehouseType; label: string; hint: string }[] = [
  { value: 'warehouse', label: 'Warehouse', hint: 'Stocks inventory' },
  { value: 'store', label: 'Retail store', hint: 'Sells direct + holds shelf stock' },
  { value: 'hub', label: 'Cross-dock hub', hint: 'Transit only — no stock' },
  { value: 'virtual', label: 'Virtual', hint: 'Bookkeeping only' },
  { value: 'third_party', label: '3PL', hint: 'Operated by a partner' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, the sheet opens in edit mode and PATCHes this record. */
  existing?: PlatformWarehouse | null;
}

function defaultForm(args: {
  isPlatformAdmin: boolean;
  tenantId?: string;
  organizationId?: string;
}): PlatformWarehouseCreatePayload {
  return {
    tenantId: args.isPlatformAdmin ? '' : (args.tenantId ?? ''),
    organizationId: !args.isPlatformAdmin && args.organizationId ? args.organizationId : '',
    code: '',
    name: '',
  };
}

function fromExisting(w: PlatformWarehouse): PlatformWarehouseCreatePayload {
  return {
    tenantId: w.tenantId,
    organizationId: w.organizationId,
    code: w.code,
    name: w.name,
    type: w.type,
    gstin: w.gstin,
    contactPerson: w.contactPerson,
    contactPhone: w.contactPhone,
    contactEmail: w.contactEmail,
    capacity: w.capacity,
    operatingHours: w.operatingHours,
    notes: w.notes,
    managerId: w.managerId,
    imageUrl: w.imageUrl,
    latitude: w.latitude,
    longitude: w.longitude,
    address: w.address,
  };
}

export function PlatformWarehouseFormSheet({ open, onOpenChange, existing }: Props) {
  const qc = useQueryClient();
  const me = useCurrentUser();
  const isPlatformAdmin = me?.tier === 'PLATFORM_ADMIN';
  const isEdit = !!existing;

  // Tenant-admin: auto-bind to their own tenant. PLATFORM_ADMIN picks
  // from the cross-tenant list. Edit mode pre-fills from the record.
  const [form, setForm] = useState<PlatformWarehouseCreatePayload>(() =>
    existing
      ? fromExisting(existing)
      : defaultForm({
          isPlatformAdmin,
          tenantId: me?.tenantId,
          organizationId: me?.organizationId,
        }),
  );

  // Re-seed form whenever we re-open (different row clicked, or
  // create→edit toggle). Without this, the previous record's state
  // leaks into the next open.
  useEffect(() => {
    if (!open) return;
    setForm(
      existing
        ? fromExisting(existing)
        : defaultForm({
            isPlatformAdmin,
            tenantId: me?.tenantId,
            organizationId: me?.organizationId,
          }),
    );
  }, [open, existing, isPlatformAdmin, me?.tenantId, me?.organizationId]);

  // Manager picker — tenant members (excluding archived) to choose
  // from. Scoped to the tenant the warehouse belongs to. Lazy-load
  // only when the sheet is open so closed warehouses don't poll.
  const managersQuery = useQuery({
    queryKey: ['platform', 'users', 'forManagerSelect', form.tenantId],
    queryFn: () =>
      platformUsersService.list({
        tenantId: form.tenantId || undefined,
        status: 'active',
        pageSize: 200,
      }),
    enabled: open && !!form.tenantId,
  });
  const managers = managersQuery.data?.items ?? [];

  const mutation = useMutation({
    mutationFn: (payload: PlatformWarehouseCreatePayload) => {
      if (isEdit) {
        // Strip immutable fields — Update DTO whitelists only mutable
        // ones, so tenantId/code in the payload trip the BE's
        // forbidNonWhitelisted validator with the unhelpful
        // "property X should not exist" message.
        const { tenantId: _t, code: _c, ...updatePayload } = payload;
        void _t;
        void _c;
        return platformWarehousesService.update(existing!.id, updatePayload);
      }
      return platformWarehousesService.create(payload);
    },
    onSuccess: (saved) => {
      toast.success(`Warehouse ${saved.code} ${isEdit ? 'updated' : 'created'}`);
      qc.invalidateQueries({ queryKey: ['platform', 'warehouses'] });
      onOpenChange(false);
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Strip empty-string tenantId so the BE's optional @IsUUID validator
    // doesn't reject the payload. Tenant-admins always have a tenantId
    // (auto-bound from session); platform-admins must pick one.
    const payload = { ...form };
    if (!payload.tenantId) delete (payload as Partial<typeof form>).tenantId;
    mutation.mutate(payload);
  };

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? `Edit ${existing!.code}` : 'Add warehouse'}
      description="Physical fulfilment location. Belongs to an organization within a tenant."
      submitLabel={isEdit ? 'Save changes' : 'Create warehouse'}
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      // Lock height so the ScrollArea works once GSTIN / manager /
      // hours / address / photo / notes push the form past viewport.
      fixedHeight="lg"
    >
      {/* TenantSelect is PLATFORM_ADMIN-only — /platform/tenants returns
          401 for tenant-admins, which renders as "Failed to load tenants"
          and blocks submission. Tenant-admins have exactly one tenant
          (their own), so we auto-bind from the session instead. */}
      {isPlatformAdmin && (
        <FormField label="Tenant" htmlFor="tenantId" required>
          <TenantSelect
            id="tenantId"
            value={form.tenantId}
            onChange={(v) =>
              setForm((f) => ({ ...f, tenantId: v, organizationId: '' }))
            }
          />
        </FormField>
      )}

      <FormField label="Organization" htmlFor="organizationId" required>
        <OrganizationSelect
          id="organizationId"
          tenantId={form.tenantId}
          value={form.organizationId}
          onChange={(v) => setForm((f) => ({ ...f, organizationId: v }))}
        />
      </FormField>

      <div className="grid grid-cols-3 gap-3">
        <FormField label="Code" htmlFor="code" required hint="e.g. WH-MUM-A.">
          <Input
            id="code"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="WH-MUM-A"
            required
          />
        </FormField>
        <FormField label="Type" htmlFor="type">
          <Select
            value={form.type ?? 'warehouse'}
            onValueChange={(v) =>
              setForm((f) => ({ ...f, type: v as PlatformWarehouseType }))
            }
          >
            <SelectTrigger id="type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WAREHOUSE_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  <span className="font-medium">{t.label}</span>
                  <span className="ml-2 text-[10.5px] text-muted-foreground">{t.hint}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Capacity" htmlFor="capacity" hint="Optional — sq ft or units.">
          <Input
            id="capacity"
            value={form.capacity ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
            placeholder="12,000 sq ft"
          />
        </FormField>
      </div>

      <FormField label="Display name" htmlFor="name" required>
        <Input
          id="name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Mumbai West Hub"
          required
        />
      </FormField>

      <FormField
        label="Warehouse GSTIN"
        htmlFor="whGstin"
        hint="Set when this warehouse holds its own GST registration (multi-state orgs). Overrides org GSTIN as Place-of-Supply on invoices."
      >
        <Input
          id="whGstin"
          value={form.gstin ?? ''}
          onChange={(e) =>
            setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))
          }
          placeholder="27AAACA1234A1Z5"
          maxLength={15}
          className="font-mono"
        />
      </FormField>

      <div className="grid grid-cols-3 gap-3">
        <FormField label="Contact person" htmlFor="contactPerson">
          <Input
            id="contactPerson"
            value={form.contactPerson ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, contactPerson: e.target.value }))}
          />
        </FormField>
        <FormField label="Contact phone" htmlFor="contactPhone">
          <Input
            id="contactPhone"
            value={form.contactPhone ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, contactPhone: e.target.value }))}
          />
        </FormField>
        <FormField label="Contact email" htmlFor="contactEmail">
          <Input
            id="contactEmail"
            type="email"
            value={form.contactEmail ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FormField
          label="Operating hours"
          htmlFor="operatingHours"
          hint="Free-text. e.g. Mon–Sat 9:00–18:00 IST."
        >
          <Input
            id="operatingHours"
            value={form.operatingHours ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, operatingHours: e.target.value }))}
            placeholder="Mon–Sat 9:00–18:00 IST"
          />
        </FormField>
        <FormField label="Manager" htmlFor="managerId">
          <Select
            value={form.managerId ?? '__none'}
            onValueChange={(v) =>
              setForm((f) => ({ ...f, managerId: v === '__none' ? undefined : v }))
            }
          >
            <SelectTrigger id="managerId">
              <SelectValue placeholder="Unassigned" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">Unassigned</SelectItem>
              {managers.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  <span className="font-medium">{m.fullName}</span>
                  <span className="ml-2 text-[10.5px] text-muted-foreground">
                    {m.email}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>

      <div className="space-y-2 rounded-md border bg-muted/30 px-3 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Address
        </div>
        <AddressFields
          idPrefix="whAddr"
          value={form.address}
          onChange={(addr) => setForm((f) => ({ ...f, address: addr }))}
        />
      </div>

      <ImageUploadField
        label="Warehouse photo"
        hint="Optional reference image — helpful for new operators to recognise the location. Up to 5 MB."
        scope="warehouse-photos"
        ownerId={existing?.id}
        kind="photo"
        value={form.imageUrl}
        onChange={(url) => setForm((f) => ({ ...f, imageUrl: url ?? undefined }))}
      />

      <FormField
        label="Notes"
        htmlFor="notes"
        hint="Free-text — dock info, directions, special handling instructions."
      >
        <Textarea
          id="notes"
          rows={3}
          value={form.notes ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          placeholder="Loading dock at rear entrance. Reception closes at 17:00 — call manager for after-hours pickup."
        />
      </FormField>
    </FormSheet>
  );
}
